import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardBlog() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '', excerpt: '', content: '', image_url: '', video_url: '', category: '', tags: [], published: false
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    loadPosts();
  }, [navigate]);

  const loadPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/blog?published_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli articoli');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) { toast.error('Compila i campi obbligatori'); return; }
    try {
      const token = localStorage.getItem('token');
      if (editingPost) {
        await axios.put(`${BACKEND_URL}/api/blog/${editingPost.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Articolo aggiornato');
      } else {
        await axios.post(`${BACKEND_URL}/api/blog`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Articolo creato');
      }
      setDialogOpen(false);
      setEditingPost(null);
      setFormData({ title: '', excerpt: '', content: '', image_url: '', video_url: '', category: '', tags: [], published: false });
      loadPosts();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title, excerpt: post.excerpt, content: post.content,
      image_url: post.image_url || '', video_url: post.video_url || '',
      category: post.category, tags: post.tags, published: post.published
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({ title: '', excerpt: '', content: '', image_url: '', video_url: '', category: '', tags: [], published: false });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-blog">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                {t('dashboard', 'tips')}
              </h1>
              <p className="text-gray-600">{t('dashboard', 'tipsDesc')}</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0066b2] hover:bg-[#005494]" onClick={resetForm} data-testid="create-post-button">
                  <Plus className="w-5 h-5 mr-2" /> Nuovo Articolo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="post-dialog">
                <DialogHeader>
                  <DialogTitle>{editingPost ? 'Modifica Articolo' : 'Nuovo Articolo'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Titolo *</label>
                    <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="post-title-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Estratto</label>
                    <Textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} rows={3} data-testid="post-excerpt-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Contenuto *</label>
                    <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} rows={10} data-testid="post-content-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">URL Immagine</label>
                      <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://example.com/image.jpg" data-testid="post-image-input" />
                      {formData.image_url && (
                        <div className="mt-2 rounded-lg overflow-hidden border border-gray-200">
                          <img
                            src={formData.image_url}
                            alt="Anteprima"
                            className="w-full h-32 object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            data-testid="post-image-preview"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">URL Video</label>
                      <Input value={formData.video_url} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })} data-testid="post-video-input" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Categoria</label>
                    <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} data-testid="post-category-input" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.published} onCheckedChange={(checked) => setFormData({ ...formData, published: checked })} data-testid="post-published-checkbox" />
                    <label className="text-sm font-medium">Pubblica</label>
                  </div>
                  <Button onClick={handleSubmit} className="w-full bg-[#0066b2] hover:bg-[#005494]" data-testid="save-post-button">
                    {editingPost ? 'Aggiorna' : 'Crea'} Articolo
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" data-testid="posts-loading" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="posts-empty">Nessun articolo</div>
          ) : (
            <div className="space-y-4">
              {posts.map((post, idx) => (
                <div key={post.id} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm flex gap-4 items-start" data-testid={`post-item-${idx}`}>
                  {post.image_url && (
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      referrerPolicy="no-referrer"
                      data-testid={`post-thumb-${idx}`}
                    />
                  )}
                  {!post.image_url && (
                    <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-bold text-gray-900 truncate" style={{ fontFamily: 'Space Grotesk' }}>{post.title}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${post.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {post.published ? 'Pubblicato' : 'Bozza'}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-2 line-clamp-2">{post.excerpt}</p>
                    <div className="text-xs text-gray-500">{post.category} | Views: {post.views}</div>
                  </div>
                  <div className="flex gap-2 ml-4 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(post)} data-testid={`edit-post-${idx}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
